import { type ReactNode } from 'react'
import { useAuth } from './AuthProvider'

/**
 * Overlays a blocking screen on top of the app when the user's account_status
 * is not 'active'. Shows the app faintly in the background (opacity + blur)
 * so the user can tell they're logged in, but nothing is interactive.
 *
 * States:
 *   pending   → "A aguardar aprovação"
 *   suspended → "Conta suspensa"
 *   cancelled → "Conta cancelada"
 */
export function AccountStatusGate({ children }: { children: ReactNode }) {
  const { accountStatus, statusReason, signOut } = useAuth()

  // Active or still loading → render app normally
  if (accountStatus === null || accountStatus === 'active') {
    return <>{children}</>
  }

  const copy = {
    pending: {
      title: 'A aguardar aprovação',
      subtitle:
        'A tua conta está a ser revista pela equipa. Vais receber acesso assim que for aprovada.',
      emoji: '⏳',
    },
    suspended: {
      title: 'Conta suspensa',
      subtitle:
        'O teu acesso foi temporariamente suspenso. Contacta-nos se achas que isto é um engano.',
      emoji: '⏸',
    },
    cancelled: {
      title: 'Conta cancelada',
      subtitle:
        'O acesso a esta conta foi cancelado permanentemente.',
      emoji: '⊘',
    },
  }[accountStatus]

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      {/* Faded app in the background */}
      <div
        style={{
          filter: 'blur(6px) saturate(0.6)',
          opacity: 0.35,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
        aria-hidden="true"
      >
        {children}
      </div>

      {/* Blocking overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: 'rgba(245, 240, 232, 0.7)',
          backdropFilter: 'blur(8px)',
          zIndex: 1000,
        }}
      >
        <div
          style={{
            maxWidth: '440px',
            width: '100%',
            background: 'var(--cream)',
            border: '1px solid var(--border)',
            borderRadius: '16px',
            padding: '40px 32px',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(26, 24, 20, 0.08)',
          }}
        >
          <div
            style={{
              fontSize: '48px',
              marginBottom: '16px',
              color: 'var(--accent)',
              fontFamily: 'var(--serif)',
              lineHeight: 1,
            }}
          >
            {copy.emoji}
          </div>
          <h1
            style={{
              fontFamily: 'var(--serif)',
              fontSize: '28px',
              fontWeight: 600,
              color: 'var(--ink)',
              margin: '0 0 12px',
            }}
          >
            {copy.title}
          </h1>
          <p
            style={{
              fontFamily: 'var(--sans)',
              fontSize: '14px',
              lineHeight: 1.6,
              color: 'var(--ink-light)',
              margin: '0 0 24px',
            }}
          >
            {copy.subtitle}
          </p>

          {statusReason && (
            <div
              style={{
                background: 'rgba(255,255,255,0.6)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '12px 16px',
                fontFamily: 'var(--sans)',
                fontSize: '13px',
                color: 'var(--ink-light)',
                textAlign: 'left',
                marginBottom: '20px',
              }}
            >
              <div
                style={{
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--ink-muted)',
                  marginBottom: '4px',
                }}
              >
                Nota do admin
              </div>
              {statusReason}
            </div>
          )}

          <button
            onClick={signOut}
            style={{
              background: 'none',
              border: 'none',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              color: 'var(--ink-muted)',
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            }}
          >
            Terminar sessão
          </button>
        </div>
      </div>
    </div>
  )
}
